import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Upload as UploadIcon,
  File,
  CheckCircle,
  XCircle,
  FolderPlus,
  FileText,
  Trash2,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { coiAPI } from '../services/api';

/**
 * CoiProjectUpload page — route: /project/coi/new
 *
 * Single-screen form for the COI / Eukaryota (QIIME 2) pipeline. Inputs:
 *   1. Project name
 *   2. ZIP file(s) containing barcodeXX/*.fastq(.gz)
 *   3. manifest.csv  (required — used by `qiime tools import`)
 *   4. metadata.csv  (optional — used only by abundance_graphs.R for figures)
 *
 * On Start:
 *   create project → upload zips → upload manifest → upload metadata (if any)
 *   → start analysis → redirect to /dashboard
 */
export default function CoiProjectUpload() {
  const navigate = useNavigate();

  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [zipFiles, setZipFiles] = useState([]);
  const [manifestFile, setManifestFile] = useState(null);
  const [metadataFile, setMetadataFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [phase, setPhase] = useState('');
  const [progress, setProgress] = useState(0);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleZipSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    setZipFiles((prev) => [...prev, ...newFiles]);
    e.target.value = '';
  };

  const removeZip = (idx) =>
    setZipFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!projectName.trim()) return setError('Please enter a project name.');
    if (zipFiles.length === 0)
      return setError('Please select at least one ZIP file containing barcode folders.');
    if (!manifestFile) return setError('manifest.csv is required for the COI pipeline.');

    setLoading(true);
    try {
      setPhase('Creating project...');
      const projRes = await coiAPI.createProject(projectName.trim(), projectDescription.trim());
      const projectId = projRes.data.id;

      setPhase('Uploading FASTQ ZIP file(s)...');
      setProgress(0);
      await coiAPI.uploadFiles(projectId, zipFiles, (e) => {
        if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
      });

      setPhase('Uploading manifest.csv...');
      setProgress(0);
      await coiAPI.uploadManifest(projectId, manifestFile, (e) => {
        if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
      });

      if (metadataFile) {
        setPhase('Uploading metadata.csv...');
        setProgress(0);
        await coiAPI.uploadMetadata(projectId, metadataFile, (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        });
      }

      setPhase('Starting QIIME 2 pipeline...');
      const startRes = await coiAPI.startAnalysis(projectId);
      setSuccess('Analysis started! Redirecting...');
      setTimeout(() => navigate(`/dashboard`), 1500);
      return startRes;
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Something went wrong.';
      setError(detail);
    } finally {
      setLoading(false);
      setPhase('');
      setProgress(0);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12 max-w-3xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-10"
      >
        <h1 className="text-4xl font-bold mb-3">New COI / Eukaryota Analysis</h1>
        <p className="text-muted-foreground">
          QIIME 2 pipeline for COI primer eDNA datasets.
        </p>
      </motion.div>

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 p-4 rounded-lg flex items-start gap-3 mb-6">
          <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive p-4 rounded-lg flex items-start gap-3 mb-6">
          <XCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="space-y-6">
        {/* Project details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FolderPlus className="h-4 w-4" />
              Project Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-sm font-medium">Project Name</label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g., eDNA Survey Aug 2025"
                disabled={loading}
                className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background disabled:opacity-50"
              />
            </div>
            <div>
              <label className="text-sm font-medium">
                Description <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                disabled={loading}
                className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background disabled:opacity-50"
              />
            </div>
          </CardContent>
        </Card>

        {/* ZIP file upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <UploadIcon className="h-4 w-4" />
              FASTQ ZIP <span className="text-destructive text-sm">*</span>
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              ZIP must contain <code className="text-xs bg-muted px-1 rounded">barcode01/</code>,{' '}
              <code className="text-xs bg-muted px-1 rounded">barcode02/</code>, ... folders with
              .fastq or .fastq.gz files inside.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <label
              htmlFor="coi-zip-input"
              className={`border-2 border-dashed border-border hover:border-primary rounded-xl p-6 text-center block cursor-pointer transition-all hover:bg-primary/5 ${
                loading ? 'pointer-events-none opacity-50' : ''
              }`}
            >
              <div className="flex justify-center mb-2">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <UploadIcon className="h-5 w-5 text-primary" />
                </div>
              </div>
              <span className="text-primary font-medium">Choose ZIP file(s)</span>
            </label>
            <input
              id="coi-zip-input"
              type="file"
              multiple
              accept=".zip"
              onChange={handleZipSelect}
              className="hidden"
              disabled={loading}
            />
            {zipFiles.length > 0 && (
              <div className="space-y-1.5 max-h-36 overflow-y-auto">
                <p className="text-sm font-medium">{zipFiles.length} file(s) selected</p>
                {zipFiles.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between bg-muted/50 px-3 py-1.5 rounded-lg"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <File className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-sm truncate">{f.name}</span>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        ({formatFileSize(f.size)})
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeZip(i)}
                      disabled={loading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Manifest */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4" />
              Manifest CSV <span className="text-destructive text-sm">*</span>
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              QIIME 2 single-end manifest (sample-id, absolute-filepath, direction).
            </p>
          </CardHeader>
          <CardContent>
            <label
              htmlFor="coi-manifest-input"
              className={`border-2 border-dashed border-border hover:border-primary rounded-lg p-3 text-center block cursor-pointer transition-all hover:bg-primary/5 ${
                loading ? 'pointer-events-none opacity-50' : ''
              }`}
            >
              {manifestFile ? (
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">{manifestFile.name}</span>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">Choose manifest.csv</span>
              )}
            </label>
            <input
              id="coi-manifest-input"
              type="file"
              accept=".csv"
              onChange={(e) => setManifestFile(e.target.files[0] || null)}
              className="hidden"
              disabled={loading}
            />
          </CardContent>
        </Card>

        {/* Optional metadata */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4" />
              Metadata CSV{' '}
              <span className="text-muted-foreground font-normal text-sm">(optional)</span>
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Used only to generate the metadata-based R figures.
            </p>
          </CardHeader>
          <CardContent>
            <label
              htmlFor="coi-metadata-input"
              className={`border-2 border-dashed border-border hover:border-primary rounded-lg p-3 text-center block cursor-pointer transition-all hover:bg-primary/5 ${
                loading ? 'pointer-events-none opacity-50' : ''
              }`}
            >
              {metadataFile ? (
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">{metadataFile.name}</span>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">Choose metadata.csv</span>
              )}
            </label>
            <input
              id="coi-metadata-input"
              type="file"
              accept=".csv"
              onChange={(e) => setMetadataFile(e.target.files[0] || null)}
              className="hidden"
              disabled={loading}
            />
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex flex-col items-center gap-3">
          {loading && (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {phase} {progress > 0 && `(${progress}%)`}
            </div>
          )}
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={loading}
            className="min-w-[220px]"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Working...
              </>
            ) : (
              <>Upload &amp; Run COI Pipeline</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
